import { useState, useRef, useEffect } from "react";
import { Calendar, ChevronDown } from "lucide-react";

/* Financial-year dropdown — the single-pane sibling of MonthPicker.
 *
 * Four screens had a native <select> here (Bookings, Income, Incentives,
 * Performance). Native selects take over the screen on mobile and ignore
 * styling on desktop, which is why MonthPicker exists; this keeps the two
 * consistent.
 *
 * Positioning is copied from MonthPicker rather than reinvented: measure
 * against <main>, not the window, because AdminShell wraps every route in
 * overflow-y-auto and CSS forces overflow-x to auto alongside it, so <main>
 * clips both ways and the sidebar puts its left edge ~240px in. Flip above
 * the trigger when there is no room below, and flip alignment when the
 * chosen side is too tight.
 *
 * `value` is the FY start year as a number; onChange returns a number. */

const POPOVER_W = 200;
const POPOVER_H = 280;

export function YearPicker({ value, onChange, options = [], align = "left" }) {
  const [open, setOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const [flipAlign, setFlipAlign] = useState(false);
  const scrollRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (!open || !triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const box = triggerRef.current.closest("main")?.getBoundingClientRect()
      || { left: 0, right: window.innerWidth, bottom: window.innerHeight, top: 0 };

    setDropUp(box.bottom - r.bottom < POPOVER_H && r.top - box.top > POPOVER_H);

    const roomForRight = r.right - box.left;
    const roomForLeft = box.right - r.left;
    setFlipAlign(
      align === "right"
        ? roomForRight < POPOVER_W && roomForLeft >= POPOVER_W
        : roomForLeft < POPOVER_W && roomForRight >= POPOVER_W,
    );
  }, [open, align]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const id = setTimeout(() => {
      scrollRef.current?.querySelector(`[data-year="${value}"]`)
        ?.scrollIntoView({ block: "center", behavior: "auto" });
    }, 0);
    return () => clearTimeout(id);
  }, [open, value]);

  const effectiveAlign = flipAlign ? (align === "right" ? "left" : "right") : align;
  const dropdownAlign = effectiveAlign === "right" ? "right-0" : "left-0";
  const label = (y) => `FY ${y}\u2013${String(y + 1).slice(2)}`;

  return (
    <div className="relative inline-block">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900 bg-white ring-1 ring-inset ring-slate-300 rounded-lg px-3 py-1.5 hover:bg-slate-50 focus:outline-none focus:ring-blue-500"
      >
        <Calendar className="w-3.5 h-3.5 text-slate-500" />
        {label(value)}
        <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className={`absolute z-40 ${dropUp ? "bottom-full mb-1" : "mt-1"} ${dropdownAlign} w-[200px] max-w-[calc(100vw-1.5rem)] bg-white rounded-lg shadow-lg ring-1 ring-slate-200 overflow-hidden`}>
            <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400 border-b border-slate-100">
              Financial year
            </div>
            <ul ref={scrollRef} className="max-h-[min(240px,45vh)] overflow-y-auto py-1" role="listbox">
              {options.map((y) => (
                <li key={y} data-year={y} role="option" aria-selected={y === value}>
                  <button
                    type="button"
                    onClick={() => { onChange(y); setOpen(false); }}
                    className={`w-full text-left text-sm px-3 py-1.5 transition-colors tabular-nums ${
                      y === value ? "bg-blue-600 text-white font-semibold" : "text-slate-700 hover:bg-slate-100"}`}
                  >
                    {label(y)}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
